import { use, useEffect, useState } from 'react';
import { useForm, UseFormReturn, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import WanderingBoundary from 'features/wandering-boundary-settings/domain/models/wandering-boundary';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';

export type WanderingBoundaryRowProviderState = {
  wanderingBoundary: WanderingBoundary;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  form: UseFormReturn<WanderingBoundary>;
  submitForm: () => void;
  validationRules: {
    name: {
      required: string;
      validate: (value: string) => boolean | string;
    };
  };
  viewingMode: ViewingMode;
  setViewingMode: (viewingMode: ViewingMode) => void;
  startEditing: () => void;
  stopEditing: () => void;
  cancelCreateWanderingBoundary: (wanderingBoundary: WanderingBoundary) => void;
};

const useWanderingBoundaryRowProviderState = (
  wanderingBoundary: WanderingBoundary,
  initialViewingMode: ViewingMode,
): WanderingBoundaryRowProviderState => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { detailsState } = use(WanderingBoundarySettingsProviderContext);
  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const {
    createWanderingBoundary,
    updateWanderingBoundary,
    removeWanderingBoundary,
    setIsEditing,
    setShouldShowConfirmationPopup,
    wanderingBoundaries,
  } = detailsState;

  const [isExpanded, setIsExpanded] = useState(false);
  const [viewingMode, setViewingMode] = useState(initialViewingMode);

  const validationRules = {
    name: {
      required: t('wanderingBoundaryValidationRules.nameRequired'),
      validate: (value: string) => {
        const existingBoundary = wanderingBoundaries?.find((boundary) => boundary.name === value && boundary.id !== wanderingBoundary.id);
        return !existingBoundary || t('wanderingBoundaryValidationRules.nameNotUnique');
      },
    },
  };

  const form = useForm<WanderingBoundary>({
    mode: 'onBlur',
    defaultValues: wanderingBoundary,
  });

  const { isDirty } = useFormState({ control: form.control });

  const submitForm = () => {
    const formData = form.getValues();

    if (formData.id) {
      updateWanderingBoundary(formData);
    } else {
      createWanderingBoundary(formData);
    }

    setShouldShowConfirmationPopup(false);
    enablePopUpGuard(false);
    setIsExpanded(true);
    setViewingMode('viewing');
  };

  const startEditing = () => {
    setIsEditing(true);
    setViewingMode('editing');
  };

  const stopEditing = () => {
    setIsEditing(false);
    setViewingMode('viewing');
    form.reset(wanderingBoundary);
  };

  const cancelCreateWanderingBoundary = (wanderingBoundary: WanderingBoundary) => {
    setIsEditing(false);
    setShouldShowConfirmationPopup(false);
    enablePopUpGuard(false);
    form.reset(wanderingBoundary);
    removeWanderingBoundary(wanderingBoundary);
  };

  useEffect(() => {
    setShouldShowConfirmationPopup(isDirty);
    enablePopUpGuard(isDirty);
  }, [isDirty]);

  useEffect(() => {
    form.reset(wanderingBoundary);
  }, [wanderingBoundary.id]);

  return {
    wanderingBoundary,
    isExpanded,
    setIsExpanded,
    form,
    submitForm,
    validationRules,
    viewingMode,
    setViewingMode,
    startEditing,
    stopEditing,
    cancelCreateWanderingBoundary,
  };
};

export default useWanderingBoundaryRowProviderState;
